import cs from "classnames";
import * as R from "ramda";
import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { BatchedRecording, saveRecordings } from "lib/build/recordings";
import { LogItem, Result, validate, Value } from "lib/build/schema";

import { Button } from "src/common/components/Button";
import {
	Fieldset,
	Form,
	FormBody,
	FormFooter,
	Legend,
	ValidationMessage
} from "src/common/components/Form";
import {
	GridColumn,
	GridRow,
	SplitColumn,
	SplitRow
} from "src/common/components/Grid";
import { getApiErrorData, isApiErrorWithCode } from "src/common/utils/api";
import { isTodayOrBefore } from "src/common/utils/dates";
import { saveFiles } from "src/common/utils/files";
import { useConfirm } from "src/features/confirm";
import { useRefetchEvents as useRefetchEventsDefault } from "src/features/events";
import { Attachments as AttachmentsField } from "src/features/forms/components/Attachments";
import { FieldsetRenderer } from "src/features/forms/components/FieldsetRenderer";
import { FieldsetSkeleton } from "src/features/forms/components/FieldsetSkeleton";
import { Timestamp as TimestampField } from "src/features/forms/components/Timestamp";
import { FormContextProvider } from "src/features/forms/context/FormContext";
import { useGetDefaultTimestamp } from "src/features/forms/hooks/useGetDefaultTimestamp";
import { useValidatorOptions } from "src/features/forms/hooks/useValidatorOptions";
import { MiniFormData } from "src/features/forms/types";
import { mapLogItemsToEventQueries } from "src/features/forms/utils/mapLogItemsToEventQueries";
import {
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { PatientLink } from "src/features/patient";
import { genericMessages } from "src/messages/generic";

import "./MiniFormWizard.css";

const messages = defineMessages({
	attachments: {
		id: "miniform_wizard.attachments",
		defaultMessage: "Leletek, mellékletek"
	},
	filledForms: {
		id: "miniform_wizard.filled_forms",
		defaultMessage: "Kitöltött űrlapok"
	},
	noFilledForms: {
		id: "miniform_wizard.no_filled_forms",
		defaultMessage: "Nincsenek kitöltött űrlapok"
	},
	skippedForms: {
		id: "miniform_wizard.skipped_forms",
		defaultMessage: "Kihagyott űrlapok"
	},
	noSkippedForms: {
		id: "miniform_wizard.no_skipped_forms",
		defaultMessage: "Nincsenek kihagyott űrlapok"
	},
	success: {
		id: "miniform_wizard.save_list_success",
		defaultMessage: "Az adatok rögzítése sikeresen megtörtént"
	},
	versionMismatchError: {
		id: "miniform_wizard.version_mismatch_error",
		defaultMessage:
			'Ezzel az időponttal a(z) "{form}" űrlap már kitöltésre került. Szeretné felülírni az előző mentést?'
	}
});

interface MiniFormWizardState {
	isValidating: boolean;
	isSaving: boolean;
	items: MiniFormWizardItemState[];
	index: number;
}

interface MiniFormWizardItemState {
	form: MiniFormData;
	isSkipped: boolean;
	isSubmitted: boolean;
	timestamp: string | undefined;
	files: File[];
	result: Result;
}

type MiniFormWizardAction =
	| MiniFormWizardBeginValidationAction
	| MiniFormWizardEndValidationAction
	| MiniFormWizardBeginSavingAction
	| MiniFormWizardEndSavingAction
	| MiniFormWizardUpdateTimestampAction
	| MiniFormWizardUpdateFilesActions
	| MiniFormWizardUpdateResultAction
	| MiniFormWizardGoToPrevAction
	| MiniFormWizardGoToNextAction
	| MiniFormWizardSkipOneAction
	| MiniFormWizardSkipAllAction;

interface MiniFormWizardBeginValidationAction {
	type: "begin-validation";
}

interface MiniFormWizardEndValidationAction {
	type: "end-validation";
}

interface MiniFormWizardBeginSavingAction {
	type: "begin-saving";
}

interface MiniFormWizardEndSavingAction {
	type: "end-saving";
}

interface MiniFormWizardUpdateTimestampAction {
	type: "update-timestamp";
	payload: { index: number; timestamp: string | undefined };
}

interface MiniFormWizardUpdateFilesActions {
	type: "update-files";
	payload: { index: number; files: File[] };
}

interface MiniFormWizardUpdateResultAction {
	type: "update-result";
	payload: { index: number; result: Result };
}

interface MiniFormWizardGoToNextAction {
	type: "go-to-next";
}

interface MiniFormWizardGoToPrevAction {
	type: "go-to-prev";
}

interface MiniFormWizardSkipOneAction {
	type: "skip-one";
}

interface MiniFormWizardSkipAllAction {
	type: "skip-all";
}

const MiniFormWizardContext = React.createContext<{
	state: MiniFormWizardState;
	dispatch: React.Dispatch<MiniFormWizardAction>;
}>({
	state: { isValidating: false, isSaving: false, items: [], index: 0 },
	dispatch() {}
});

interface MiniFormWizardProps {
	patientId: string;
	forms: MiniFormData[];
}

export const MiniFormWizard: React.FunctionComponent<MiniFormWizardProps> = ({
	patientId,
	forms
}) => {
	const [state, dispatch] = useMiniFormWizardState(forms);
	return (
		<MiniFormWizardContext.Provider value={{ state, dispatch }}>
			{state.items.map((_, i) => (
				<div
					key={i}
					className={cs("miniform-wizard-item", {
						"miniform-wizard-item--active": i === state.index
					})}
				>
					<MiniFormWizardItem index={i} />
				</div>
			))}
			<div
				className={cs("miniform-wizard-item", {
					"miniform-wizard-item--active":
						state.index === state.items.length
				})}
			>
				<MiniFormWizardSummary patientId={patientId} />
			</div>
		</MiniFormWizardContext.Provider>
	);
};

const emptyItem = {
	isSkipped: false,
	isSubmitted: false,
	timestamp: undefined,
	files: [],
	result: { value: {} }
};

const emptyLogItems: LogItem[] = [];
const emptyValue: Value = {};

interface MiniFormWizardItemProps {
	index: number;
}

const MiniFormWizardItem: React.FunctionComponent<MiniFormWizardItemProps> = ({
	index
}) => {
	const { state, dispatch } = React.useContext(MiniFormWizardContext);

	const { isValidating, items } = state;
	const {
		isSubmitted,
		timestamp,
		files,
		form: {
			title,
			fieldset,
			showAttachments = true,
			showTimeInput = false,
			validator
		},
		result
	} = items[index];
	const {
		value,
		messages = emptyLogItems,
		warnings = emptyLogItems,
		errors = emptyLogItems
	} = result;

	const isValid = React.useMemo(
		() =>
			timestamp !== undefined &&
			isTodayOrBefore(timestamp) &&
			errors.length === 0,
		[timestamp, errors]
	);

	const [isInitialized, setInitialized] = React.useState(isSubmitted);

	const onChangeTimestamp = React.useCallback(
		(timestamp: string) => {
			dispatch({
				type: "update-timestamp",
				payload: { index, timestamp }
			});
		},
		[dispatch, index]
	);

	const onChangeFiles = React.useCallback(
		(files: File[]) => {
			dispatch({ type: "update-files", payload: { index, files } });
		},
		[dispatch, index]
	);

	const valueRef = React.useRef(value);

	React.useEffect(() => {
		valueRef.current = value;
	}, [value]);

	const validatorOptions = useValidatorOptions();

	const onChangeValue = React.useCallback(
		async (
			valueOrFn: Value | ((value: Value) => Value),
			signal?: AbortSignal
		) => {
			dispatch({ type: "begin-validation" });
			const result = await validate({
				validator,
				value:
					typeof valueOrFn === "function"
						? valueOrFn(valueRef.current)
						: valueOrFn,
				options: validatorOptions
			});
			if (signal === undefined || !signal.aborted) {
				setInitialized(true);
				dispatch({ type: "end-validation" });
				dispatch({ type: "update-result", payload: { index, result } });
			}
		},
		[dispatch, validator, validatorOptions, index]
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only re-fire on wizard step change; including onChangeValue/state would cause infinite loops
	React.useEffect(() => {
		const controller = new AbortController();
		const {
			isSubmitted,
			form: { initialValue = emptyValue },
			result: { value: prevValue }
		} = state.items[state.index];
		if (isSubmitted) {
			onChangeValue(prevValue, controller.signal);
		} else {
			onChangeValue(initialValue, controller.signal);
		}
		return () => {
			controller.abort();
		};
	}, [index]);

	const onClickGoToPrev = React.useCallback(() => {
		dispatch({ type: "go-to-prev" });
	}, [dispatch]);

	const onClickGoToNext = React.useCallback(() => {
		dispatch({ type: "go-to-next" });
	}, [dispatch]);

	const onClickSkipOne = React.useCallback(() => {
		dispatch({ type: "skip-one" });
	}, [dispatch]);

	const onClickSkipAll = React.useCallback(() => {
		dispatch({ type: "skip-all" });
	}, [dispatch]);

	return (
		<Form isSubmitted={isSubmitted}>
			<FormBody>
				<Fieldset>
					<GridRow>
						<GridColumn columnSize={6}>
							<TimestampField
								showTimeInput={showTimeInput}
								timestamp={timestamp}
								onChangeTimestamp={onChangeTimestamp}
							/>
						</GridColumn>
					</GridRow>
				</Fieldset>
				<Fieldset>
					<Legend>
						<FormattedMessage {...title} />
					</Legend>
					{isInitialized ? (
						<FormContextProvider
							isInitialized={isInitialized}
							value={value}
							setValue={onChangeValue}
							messages={messages}
							warnings={warnings}
							errors={errors}
						>
							<FieldsetRenderer {...fieldset} />
						</FormContextProvider>
					) : (
						<FieldsetSkeleton />
					)}
				</Fieldset>
				{showAttachments && (
					<Fieldset>
						<AttachmentsField
							unsavedFiles={files}
							onChangeUnsavedFiles={onChangeFiles}
						/>
					</Fieldset>
				)}
			</FormBody>
			<FormFooter>
				<SplitRow>
					<SplitColumn side="left">
						<PatientLink
							to="/dashboard"
							className="button button--secondary"
						>
							<FormattedMessage {...genericMessages.cancel} />
						</PatientLink>
						{index !== 0 && (
							<Button
								type="button"
								variant="secondary"
								disabled={isValidating}
								onClick={onClickGoToPrev}
							>
								<FormattedMessage {...genericMessages.back} />
							</Button>
						)}
					</SplitColumn>
					<SplitColumn side="right">
						{isSubmitted && !isValid && (
							<ValidationMessage>
								<FormattedMessage
									{...genericMessages.fixValidationErrors}
								/>
							</ValidationMessage>
						)}
						<Button
							type="button"
							variant="secondary"
							onClick={onClickSkipOne}
						>
							<FormattedMessage {...genericMessages.skip} />
						</Button>
						<Button
							type="button"
							variant="secondary"
							onClick={onClickSkipAll}
						>
							<FormattedMessage {...genericMessages.skipAll} />
						</Button>
						<Button type="submit" onClick={onClickGoToNext}>
							<FormattedMessage {...genericMessages.forward} />
						</Button>
					</SplitColumn>
				</SplitRow>
			</FormFooter>
		</Form>
	);
};

interface MiniFormWizardSummaryProps {
	patientId: string;
}

const MiniFormWizardSummary: React.FunctionComponent<
	MiniFormWizardSummaryProps
> = ({ patientId }) => {
	const { org, mod } = useOrgModParams();

	const navigate = useNavigate();
	const confirm = useConfirm();

	const { formatMessage } = useIntl();
	const createMessage = useCreateMessage();
	const createGenericError = useCreateGenericError();

	const refetchEvents = useRefetchEventsDefault(patientId);

	const { state, dispatch } = React.useContext(MiniFormWizardContext);

	const { isSaving, items } = state;

	const filledForms = React.useMemo(
		() => items.filter(({ isSkipped }) => !isSkipped),
		[items]
	);

	const skippedForms = React.useMemo(
		() => items.filter(({ isSkipped }) => isSkipped),
		[items]
	);

	const onClickGoToPrev = React.useCallback(() => {
		dispatch({ type: "go-to-prev" });
	}, [dispatch]);

	const saveRecordingsInner = React.useCallback(
		async ({
			org,
			mod,
			patientId,
			items,
			data
		}: {
			org: string;
			mod: string;
			patientId: string;
			items: MiniFormWizardItemState[];
			data: BatchedRecording[];
		}): Promise<boolean> => {
			try {
				await saveRecordings({ org, mod, patientId, data });
				return true;
			} catch (error) {
				if (
					!isApiErrorWithCode<string>(
						error,
						"recordings.save.failed.concurrent"
					)
				) {
					throw error;
				}
				const { payload: version } = getApiErrorData(error);
				const id = version.split("|")[1];
				const item = items.find(item => item.form.id === id);
				if (item === undefined) {
					throw error;
				}
				const msg = formatMessage(messages.versionMismatchError, {
					form: formatMessage(item.form.title)
				});
				if ((await confirm({ content: msg })) === true) {
					return await saveRecordingsInner({
						org,
						mod,
						patientId,
						items,
						data: updateVersion(data, id, version)
					});
				}
			}
			return false;
		},
		[confirm, formatMessage]
	);

	const onSubmit = React.useCallback(async (): Promise<void> => {
		dispatch({ type: "begin-saving" });
		try {
			const data = await prepareDataToSave({
				org,
				mod,
				patientId,
				items
			});
			const isSaved = await saveRecordingsInner({
				org,
				mod,
				patientId,
				items,
				data
			});
			dispatch({ type: "end-saving" });
			if (isSaved) {
				createMessage(messages.success);
				for (const {
					isSkipped,
					result: { messages = [] }
				} of items) {
					if (!isSkipped) {
						for (const query of mapLogItemsToEventQueries(
							messages
						)) {
							refetchEvents(query);
						}
					}
				}
				navigate(`/${org}/${mod}/patients/${patientId}/dashboard`);
			}
		} catch {
			dispatch({ type: "end-saving" });
			createGenericError();
		}
	}, [
		navigate,
		dispatch,
		createMessage,
		createGenericError,
		saveRecordingsInner,
		refetchEvents,
		org,
		mod,
		patientId,
		items
	]);

	return (
		<Form isSubmitted={true}>
			<FormBody>
				<Fieldset>
					<Legend>
						<FormattedMessage {...messages.filledForms} />:
					</Legend>
					<ul>
						{filledForms.length === 0 ? (
							<li>
								<FormattedMessage {...messages.noFilledForms} />
							</li>
						) : (
							filledForms.map(({ form: { title } }, i) => (
								<li key={i}>
									<FormattedMessage {...title} />
								</li>
							))
						)}
					</ul>
				</Fieldset>
				<Fieldset>
					<Legend>
						<FormattedMessage {...messages.skippedForms} />:
					</Legend>
					<ul>
						{skippedForms.length === 0 ? (
							<li>
								<FormattedMessage
									{...messages.noSkippedForms}
								/>
							</li>
						) : (
							skippedForms.map(({ form: { title } }, i) => (
								<li key={i}>
									<FormattedMessage {...title} />
								</li>
							))
						)}
					</ul>
				</Fieldset>
			</FormBody>
			<FormFooter>
				<SplitRow>
					<SplitColumn side="left">
						<PatientLink
							to="/dashboard"
							className="button button--secondary"
						>
							<FormattedMessage {...genericMessages.cancel} />
						</PatientLink>
						<Button
							type="button"
							variant="secondary"
							disabled={isSaving}
							onClick={onClickGoToPrev}
						>
							<FormattedMessage {...genericMessages.back} />
						</Button>
					</SplitColumn>
					<SplitColumn side="right">
						<Button
							type="button"
							isLoading={isSaving}
							onClick={onSubmit}
						>
							<FormattedMessage {...genericMessages.save} />
						</Button>
					</SplitColumn>
				</SplitRow>
			</FormFooter>
		</Form>
	);
};

function useMiniFormWizardState(
	forms: MiniFormData[]
): readonly [MiniFormWizardState, React.Dispatch<MiniFormWizardAction>] {
	const getDefaultTimestamp = useGetDefaultTimestamp();

	const initialState = React.useMemo(
		(): MiniFormWizardState => ({
			isValidating: false,
			isSaving: false,
			index: 0,
			items: forms.map(form => ({
				...emptyItem,
				form,
				timestamp: getDefaultTimestamp({
					showTimeInput: form.showTimeInput === true,
					applyCurrentDate: form.applyCurrentDate === true,
					applyDateOfBirth: form.applyDateOfBirth === true
				})
			}))
		}),
		[forms, getDefaultTimestamp]
	);

	return React.useReducer(miniFormWizardReducer, initialState);
}

function miniFormWizardReducer(
	state: MiniFormWizardState,
	action: MiniFormWizardAction
): MiniFormWizardState {
	if (action.type === "begin-validation") {
		return { ...state, isValidating: true };
	}

	if (action.type === "end-validation") {
		return { ...state, isValidating: false };
	}

	if (action.type === "begin-saving") {
		return { ...state, isSaving: true };
	}

	if (action.type === "end-saving") {
		return { ...state, isSaving: false };
	}

	if (action.type === "update-timestamp") {
		return {
			...state,
			items: R.update(
				action.payload.index,
				{
					...state.items[action.payload.index],
					timestamp: action.payload.timestamp
				},
				state.items
			)
		};
	}

	if (action.type === "update-files") {
		return {
			...state,
			items: R.update(
				action.payload.index,
				{
					...state.items[action.payload.index],
					files: action.payload.files
				},
				state.items
			)
		};
	}

	if (action.type === "update-result") {
		return {
			...state,
			items: R.update(
				action.payload.index,
				{
					...state.items[action.payload.index],
					result: action.payload.result
				},
				state.items
			)
		};
	}

	if (action.type === "go-to-prev") {
		if (state.index === 0) {
			return state;
		}
		return { ...state, index: state.index - 1 };
	}

	if (action.type === "go-to-next") {
		if (state.index === state.items.length) {
			return state;
		}
		let out = state;
		out = {
			...out,
			items: R.update(
				out.index,
				{
					...out.items[out.index],
					isSkipped: false,
					isSubmitted: true
				},
				out.items
			)
		};
		const {
			timestamp,
			result: { errors = [] }
		} = out.items[out.index];
		if (
			timestamp !== undefined &&
			isTodayOrBefore(timestamp) &&
			errors.length === 0
		) {
			out = { ...out, index: out.index + 1 };
		}
		return out;
	}

	if (action.type === "skip-one") {
		if (state.index === state.items.length) {
			return state;
		}
		return {
			...state,
			index: state.index + 1,
			items: R.update(
				state.index,
				{ ...state.items[state.index], isSkipped: true },
				state.items
			)
		};
	}

	if (action.type === "skip-all") {
		if (state.index === state.items.length) {
			return state;
		}
		let out = state;
		for (let i = state.index; i < state.items.length; i++) {
			out = {
				...out,
				items: R.update(
					i,
					{ ...out.items[i], isSkipped: true },
					out.items
				)
			};
		}
		out = { ...out, index: out.items.length };
		return out;
	}

	return state;
}

async function prepareDataToSave({
	org,
	mod,
	patientId,
	items
}: {
	org: string;
	mod: string;
	patientId: string;
	items: MiniFormWizardItemState[];
}): Promise<BatchedRecording[]> {
	const data = [];
	for (const {
		isSkipped,
		timestamp,
		files,
		form: { id: form, schemaId: schema },
		result: { value }
	} of items) {
		if (isSkipped || timestamp === undefined) {
			continue;
		}
		const attachments = await saveFiles({
			org,
			mod,
			patientId,
			files
		});
		data.push({
			form,
			schema: schema ? schema : form,
			value,
			timestamp,
			attachments
		});
	}
	return data;
}

function updateVersion(
	data: BatchedRecording[],
	form: string,
	version: string
): BatchedRecording[] {
	return data.map(item => (item.form === form ? { ...item, version } : item));
}
